/**
 * useImpression — fires a callback exactly once per element mount, when the
 * element is intersecting the viewport at ≥ `threshold` visibility for at
 * least `dwellMs` continuous milliseconds.
 *
 * This is what powers accurate "impression" (exposure) events — separate
 * from click events — so we can compute honest CTR and time-to-click
 * distributions in the experiment dashboard.
 *
 * Design notes:
 *  - Returns a ref callback (not a MutableRefObject) so it composes cleanly
 *    with other refs when the CTA also needs `React.forwardRef` support.
 *  - `dwellMs` guards against fly-by scrolls that flash a CTA on screen
 *    for a frame. Default 300ms feels honest without missing quick users.
 *  - `firstVisibleAtRef` is populated on the *first* qualifying impression
 *    so callers can compute `Date.now() - firstVisibleAt` at click time.
 *  - No-ops on SSR and when IntersectionObserver is unavailable.
 */
import * as React from "react";

export interface UseImpressionOptions {
  /** Fraction of the element that must be visible. Default 0.5. */
  threshold?: number;
  /** Continuous visible time before we count the impression. Default 300ms. */
  dwellMs?: number;
  /** Disable tracking (e.g. loading state). */
  disabled?: boolean;
}

export interface UseImpressionResult<T extends Element> {
  /** Attach to the tracked element. Composes with forwardRef via composeRefs. */
  ref: (node: T | null) => void;
  /** Timestamp (ms epoch) of the first qualifying impression, or null. */
  firstVisibleAtRef: React.MutableRefObject<number | null>;
}

export function useImpression<T extends Element = HTMLElement>(
  onImpression: () => void,
  options: UseImpressionOptions = {},
): UseImpressionResult<T> {
  const { threshold = 0.5, dwellMs = 300, disabled = false } = options;
  const firstVisibleAtRef = React.useRef<number | null>(null);
  const firedRef = React.useRef(false);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const timerRef = React.useRef<number | null>(null);
  const callbackRef = React.useRef(onImpression);
  callbackRef.current = onImpression;

  const cleanup = React.useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  const ref = React.useCallback(
    (node: T | null) => {
      cleanup();
      if (!node || disabled || firedRef.current) return;
      if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
        // Fallback: assume visible on mount.
        firstVisibleAtRef.current = Date.now();
        firedRef.current = true;
        try { callbackRef.current(); } catch { /* ignore */ }
        return;
      }
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            if (timerRef.current !== null) return;
            timerRef.current = window.setTimeout(() => {
              if (firedRef.current) return;
              firedRef.current = true;
              firstVisibleAtRef.current = Date.now();
              try { callbackRef.current(); } catch { /* ignore */ }
              cleanup();
            }, dwellMs);
          } else if (timerRef.current !== null) {
            // Left the viewport before dwell completed — reset.
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        },
        { threshold: [threshold] },
      );
      observerRef.current.observe(node);
    },
    [cleanup, disabled, dwellMs, threshold],
  );

  React.useEffect(() => cleanup, [cleanup]);

  return { ref, firstVisibleAtRef };
}
